import { useEffect, useState } from "react";
import { Ship, Search, FileSpreadsheet, Settings } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import SearchTab from "@/components/SearchTab";
import BulkUploadTab from "@/components/BulkUploadTab";
import AdminPanel from "@/components/AdminPanel";

export default function MainLayout({ user, onLogout }) {
  const [activeTab, setActiveTab] = useState("search");
  const isAdmin = user?.role === "admin";

  useEffect(() => {
    if (!isAdmin && activeTab === "admin") {
      setActiveTab("search");
    }
  }, [activeTab, isAdmin]);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="sticky top-0 z-50 glass-header border-b border-slate-200/50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-ocean-900 rounded-lg flex items-center justify-center">
                <Ship className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="font-heading font-bold text-xl text-ocean-900 tracking-tight">
                  VesselOps Control
                </h1>
                <p className="text-xs text-slate-500">Email Finder & Enrichment</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-right">
                <p className="text-sm font-medium text-ocean-900">
                  {user?.username}
                </p>
                <p className="text-xs text-slate-500 capitalize">{user?.role}</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={onLogout}
                className="border-slate-300 text-slate-600 hover:text-ocean-900"
              >
                Sign out
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="w-full max-w-md mb-8 bg-white border border-slate-200 p-1 rounded-lg shadow-sm">
            <TabsTrigger
              value="search"
              data-testid="tab-search"
              className="flex-1 flex items-center justify-center gap-2 data-[state=active]:bg-ocean-900 data-[state=active]:text-white rounded-md py-2.5 px-4 transition-all font-medium"
            >
              <Search className="w-4 h-4" />
              Single Search
            </TabsTrigger>
            <TabsTrigger
              value="bulk"
              data-testid="tab-bulk"
              className="flex-1 flex items-center justify-center gap-2 data-[state=active]:bg-ocean-900 data-[state=active]:text-white rounded-md py-2.5 px-4 transition-all font-medium"
            >
              <FileSpreadsheet className="w-4 h-4" />
              Bulk Upload
            </TabsTrigger>
            {isAdmin && (
              <TabsTrigger
                value="admin"
                data-testid="tab-admin"
                className="flex-1 flex items-center justify-center gap-2 data-[state=active]:bg-ocean-900 data-[state=active]:text-white rounded-md py-2.5 px-4 transition-all font-medium"
              >
                <Settings className="w-4 h-4" />
                Admin
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="search" className="animate-fade-in">
            <SearchTab />
          </TabsContent>

          <TabsContent value="bulk" className="animate-fade-in">
            <BulkUploadTab />
          </TabsContent>

          {isAdmin && (
            <TabsContent value="admin" className="animate-fade-in">
              <AdminPanel />
            </TabsContent>
          )}
        </Tabs>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-white mt-auto">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <p className="text-sm text-slate-500 text-center">
            VesselOps Control - Internal Vessel Email Database Tool
          </p>
        </div>
      </footer>
    </div>
  );
}
